'use client';
import { getAnonUser } from './utils';
import { getOptimizelyAttributes } from './attributes';
import Script from 'next/script';
import Cookies from 'js-cookie';
import { EventEmitter } from 'events';
import { useEffect } from 'react';
import { pushToDataLayer } from 'services/Gtm/functions';
import { sendGAEvent } from '@next/third-parties/google';
import optimizelySdk, { type OptimizelyUserContext, enums, type ListenerPayload } from '@optimizely/optimizely-sdk';
import type { OptimizelyDecisionTracking, OptimizelyTrackTracking } from 'services/Gtm/entities';
import type CustomWindow from 'interfaces/Utils';

declare let window: CustomWindow;
const optimizely = optimizelySdk.createInstance({
  sdkKey: process.env.NEXT_PUBLIC_OPTIMIZELY_KEY,
});

const userFlagsEmitter = new EventEmitter();

export interface VariantProps {
  variant?: string | null;
  enabled?: boolean;
  variables?: any;
}

export interface OptimizelyUserInfo {
  user?: OptimizelyUserContext;
  [key: string]: VariantProps | OptimizelyUserContext | undefined;
}

interface TrackingPayload {
  userId: string;
  attributes: string;
  event: {
    id: string;
    experimentId: string;
    key: string;
    tags: {
      $opt_event_properties: {
        URL: string;
        value: number;
      };
    };
  };
  eventKey: string;
  eventTags: {
    $opt_event_properties: {
      URL: string;
      value: number;
    };
  };
  eventExperiment: string;
}

interface OnDecisionNofication {
  type: string;
  userId: string;
  attributes: string;
  decisionInfo: {
    enabled: boolean;
    flagKey: string;
    ruleKey: string;
    variationKey: string;
  };
}

const currentTests = [
  {
    location: ['2025', '2024', 'article'],
    testName: 'socialShareV2',
  },
  {
    location: ['section'],
    testName: 'section',
  },
  {
    location: ['checkout'],
    testName: 'helio-setup',
  },
  {
    location: ['education'],
    testName: 'education',
  },
  {
    location: ['2025', '2024', 'article'],
    testName: 'article-replatform',
  },
  {
    location: ['*'],
    testName: 'googleOneTap',
  },
  {
    location: ['*'],
    testName: 'asiaMigration',
  },
];

const calculateTimeOnPage = () => {
  const startTime = Date.now();
  window.addEventListener('beforeunload', async () => {
    const endTime = Date.now();
    const timeOnPage = Math.round((endTime - startTime) / 1000);
    const userId = Cookies.get('pianoUid') || getAnonUser();
    const attributes = await getOptimizelyAttributes();
    const user = optimizely?.createUserContext(userId, attributes);

    const adImpressionCount = parseInt(sessionStorage.getItem('adImpressions') || '0', 10);
    if (user) {
      // @ts-ignore
      user.trackEvent('time_on_page', { value: timeOnPage });
      // @ts-ignore
      user.trackEvent('ad_impressions', { value: adImpressionCount });
      const scrollDepth = parseInt(sessionStorage.getItem('scrollDepth') || '0', 10);
      // @ts-ignore
      user.trackEvent('scroll_depth', { value: scrollDepth });
      const pagesVisited = parseInt(sessionStorage.getItem('pagesVisited') || '0', 10);
      user.trackEvent('pages_visited', { value: pagesVisited });
      sessionStorage.removeItem('pagesVisited');
      sessionStorage.removeItem('scrollDepth');
      sessionStorage.removeItem('adImpressions');
      if (timeOnPage < 10) {
        // @ts-ignore
        user.trackEvent('bounce');
      }
    }
  });
};

// calcuate scroll depth when the user scrolls
const calculateScrollDepth = () => {
  let scrollDepth = parseInt(sessionStorage.getItem('scrollDepth') || '0', 10);
  window.addEventListener('scroll', () => {
    const scrolled = window.scrollY + window.innerHeight;
    const totalHeight = document.documentElement.scrollHeight;
    const depth = Math.round((scrolled / totalHeight) * 100);
    if (depth > scrollDepth) {
      scrollDepth = depth;
      sessionStorage.setItem('scrollDepth', scrollDepth.toString());
    }
  });
};

const calculatePagesVisited = async () => {
  let pagesVisited = parseInt(sessionStorage.getItem('pagesVisited') || '0', 10);
  pagesVisited += 1;
  sessionStorage.setItem('pagesVisited', pagesVisited.toString());
};

const userFlags: OptimizelyUserInfo = {};

const addTracking = () => {
  optimizely?.notificationCenter.addNotificationListener(
    enums.NOTIFICATION_TYPES.DECISION,
    (notificationData: ListenerPayload) => {
      const { type, userId, attributes, decisionInfo } = notificationData as unknown as OnDecisionNofication;
      const trackingInfo: OptimizelyDecisionTracking = {
        attributes,
        enabled: decisionInfo.enabled,
        event: 'optimizely-decision',
        flagKey: decisionInfo.flagKey,
        ruleKey: decisionInfo.ruleKey,
        type,
        userId,
        variationKey: decisionInfo.variationKey,
      };
      sendGAEvent('event', `experiment_decision_${decisionInfo.flagKey}_${decisionInfo.variationKey}`, trackingInfo);
      window?.dbbRum?.push(['tag1', `experiment_${decisionInfo.flagKey}_${decisionInfo.variationKey}`]);
      pushToDataLayer(trackingInfo);
    },
  );

  optimizely?.notificationCenter.addNotificationListener(
    enums.NOTIFICATION_TYPES.TRACK,
    (notificationData: ListenerPayload) => {
      const { userId, attributes, eventKey, eventTags } = notificationData as unknown as TrackingPayload;
      const trackingInfo: OptimizelyTrackTracking = {
        attributes,
        event: 'optimizely-track',
        eventKey,
        userId,
        value: eventTags.$opt_event_properties.value,
      };
      const optimizelyData = window.dataLayer.filter(
        (item) => (item as OptimizelyDecisionTracking).event === 'optimizely-decision',
      );
      optimizelyData.forEach((item) => {
        const decision = item as OptimizelyDecisionTracking;
        const path = window.location.pathname;
        const currentTest = currentTests.find((test) => test.testName === decision.flagKey);
        const locationInPath = currentTest?.location.some((loc) => path.includes(loc));
        if (currentTest && locationInPath) {
          trackingInfo.eventExperiment = decision.flagKey;
          trackingInfo.eventExperimentId = decision.variationKey;
          sendGAEvent('event', `experiment_track_${decision.flagKey}_${decision.variationKey}`, trackingInfo);
          pushToDataLayer(trackingInfo);
        }
      });
    },
  );
};

void optimizely?.onReady().then(async ({ success }) => {
  if (!success || !optimizely) {
    return {};
  }
  addTracking();
  const userId = getAnonUser();
  const user = optimizely.createUserContext(userId);
  if (!user) {
    return {};
  }
  userFlags.user = user;

  for (let i = 0; i !== currentTests.length; i += 1) {
    const { testName } = currentTests[i];

    try {
      const decision = user.decide(testName);
      const urlParams = new URLSearchParams(window.location.search);
      const testOverride = urlParams.get(`${testName}`);
      const testOverrideBoolean = testOverride ? testOverride.toLowerCase() !== 'off' : decision.enabled;
      const testOverrideVariant = testOverride ? testOverride.toLowerCase() : decision.variationKey;
      userFlags[testName] = {
        enabled: testOverrideBoolean,
        variables: decision.variables,
        variant: testOverrideVariant,
      };
    } catch {
      userFlags[testName] = {
        enabled: false,
        variables: {},
        variant: null,
      };
    }
  }
  userFlagsEmitter.emit('userFlagsUpdated', userFlags);
  return userFlags;
});
// when a user instersets with an add increment the adImpressions count in the sessions
const incrementAdImpressions = () => {
  let adImpressions = parseInt(sessionStorage.getItem('adImpressions') || '1', 10);
  adImpressions += 1;
  sessionStorage.setItem('adImpressions', adImpressions.toString());
};
// track event with properties
const trackOptimizelyEvent = async (event: string, properties: Record<string, any>) => {
  const userId = getAnonUser();
  const attributes = await getOptimizelyAttributes();
  const user = optimizely?.createUserContext(userId, attributes);
  if (user) {
    const tags = {
      $opt_event_properties: properties,
    };
    // @ts-ignore
    user.trackEvent(event, tags);
  }
};

const useIntersectionTracking = (elementId: string, properties: Record<string, any>, event: string) => {
  useEffect(() => {
    const targetElement = document.getElementById(elementId);
    if (!targetElement) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(async (entry) => {
          if (entry.isIntersecting) {
            const userId = getAnonUser();
            const attributes = await getOptimizelyAttributes();
            const user = optimizely?.createUserContext(userId, attributes);
            if (event.includes('ad')) incrementAdImpressions();
            if (user) {
              const tags = {
                $opt_event_properties: properties,
              };

              // @ts-ignore
              user.trackEvent(event, tags);
            }
          }
        });
      },
      { threshold: 1.0 }, // Fires when the entire element is in view
    );

    observer.observe(targetElement);

    return () => observer.disconnect();
  }, [elementId, properties]);
};
const OptimizelyScript: React.FC = () => (
  <Script
    src={process.env.NEXT_PUBLIC_OPTIMIZELY_URL}
    id='optimizely'
    async
    strategy='beforeInteractive'
  />
);

const NextOptimzely = () => {
  useEffect(() => {
    calculateTimeOnPage();
    calculateScrollDepth();
    calculatePagesVisited();
  }, []);
  useIntersectionTracking('edu_article_instream_ad_0', { value: 2 }, 'saw_first_ad');
  useIntersectionTracking('edu_article_instream_ad_1', { value: 2 }, 'saw_second_ad');

  return <></>;
};
export {
  userFlagsEmitter,
  userFlags,
  OptimizelyScript,
  useIntersectionTracking,
  calculateTimeOnPage,
  calculatePagesVisited,
  calculateScrollDepth,
  trackOptimizelyEvent,
  NextOptimzely,
};
